import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Heart, Sun, Shield, ArrowRight, Loader2, Star } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_ICONS = {
  'On Track': Sun,
  'Building Momentum': ArrowRight,
  'Needs Attention': Shield,
  'Support Needed': Heart,
  'Priority Support': Heart,
};

// Light, on-brand status palette aligned with the rest of the app
const STATUS_STYLES = {
  'On Track': {
    card: 'bg-emerald-50 border-emerald-200',
    icon: 'text-emerald-600',
    title: 'text-emerald-900',
    text: 'text-emerald-800/80',
  },
  'Building Momentum': {
    card: 'bg-teal-50 border-teal-200',
    icon: 'text-teal-600',
    title: 'text-teal-900',
    text: 'text-teal-800/80',
  },
  'Needs Attention': {
    card: 'bg-amber-50 border-amber-200',
    icon: 'text-amber-600',
    title: 'text-amber-900',
    text: 'text-amber-800/80',
  },
  'Support Needed': {
    card: 'bg-orange-50 border-orange-200',
    icon: 'text-orange-600',
    title: 'text-orange-900',
    text: 'text-orange-800/80',
  },
  'Priority Support': {
    card: 'bg-rose-50 border-rose-200',
    icon: 'text-rose-600',
    title: 'text-rose-900',
    text: 'text-rose-800/80',
  },
};

export default function MySupportStatusPage() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = {};

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const meRes = await fetch(`${API}/api/auth/me`, { headers, credentials: 'include' });
        const me = await meRes.json();
        const res = await fetch(`${API}/api/risk-engine/participant/${me.id}`, { headers, credentials: 'include' });
        if (res.ok && !cancelled) {
          const json = await res.json();
          if (!cancelled) setData(json);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-xl mx-auto p-6 text-center">
        <Shield className="h-12 w-12 mx-auto mb-4 text-teal-500" />
        <h2 className="text-xl font-bold text-foreground mb-2">Support Status</h2>
        <p className="text-muted-foreground">Your support plan is being set up. Check back soon.</p>
      </div>
    );
  }

  const StatusIcon = STATUS_ICONS[data.status_label] || Shield;
  const style = STATUS_STYLES[data.status_label] || STATUS_STYLES['On Track'];

  return (
    <div className="max-w-xl mx-auto p-4 sm:p-6 space-y-6" data-testid="my-support-status">
      {/* Hero Status Card */}
      <Card className={`border-0 shadow-sm rounded-2xl ${style.card} border`}>
        <CardContent className="pt-8 pb-8 text-center">
          <StatusIcon className={`h-12 w-12 mx-auto mb-4 ${style.icon}`} />
          <h1 className={`text-2xl font-bold ${style.title}`} data-testid="status-label">{data.status_label}</h1>
          <p className={`${style.text} mt-3 max-w-md mx-auto`}>{data.message}</p>
        </CardContent>
      </Card>

      {/* Strengths */}
      <Card className="border-0 shadow-sm rounded-2xl bg-white">
        <CardHeader>
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Star className="h-4 w-4 text-emerald-600" /> Your Strengths
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {data.strengths?.map((s, i) => (
              <span key={`item-${i}`} className="px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm">
                {s}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Focus Areas */}
      <Card className="border-0 shadow-sm rounded-2xl bg-white">
        <CardHeader>
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-amber-600" /> Focus Areas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {data.focus_areas?.map((f, i) => (
              <span key={`item-${i}`} className="px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-sm">
                {f}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card className="border-0 shadow-sm rounded-2xl bg-white">
        <CardHeader>
          <CardTitle className="text-foreground text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-teal-600" /> Next Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {data.next_steps?.map((step, i) => (
              <li key={`item-${i}`} className="flex items-start gap-2 text-sm text-foreground/80">
                <span className="text-teal-600 mt-0.5 shrink-0">&#10003;</span>
                {step}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Support Summaries from Team */}
      {data.support_summaries?.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl bg-teal-50 border border-teal-200">
          <CardHeader>
            <CardTitle className="text-teal-900 text-base flex items-center gap-2">
              <Heart className="h-4 w-4 text-teal-600" /> Messages from Your Support Team
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.support_summaries.map((s, i) => (
              <div key={`item-${i}`} className="bg-white rounded-lg p-3 border border-teal-100">
                <p className="text-sm text-foreground/80 italic">&ldquo;{s.text}&rdquo;</p>
                <p className="text-[10px] text-muted-foreground mt-1">{new Date(s.date).toLocaleDateString()}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
